import React, { useState, useEffect, useCallback } from 'react';
import Icon from './icons';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Who is calling the public endpoints, and how hard.
 *
 * Built because three addresses were once producing most of the self-switch
 * traffic while paying customers were turned away, and nothing in the console
 * showed it — it took reading nginx logs over SSH to find.
 *
 * Deliberately no automatic blocking. Customers behind a shared office or ISP
 * NAT look identical to a script from here, so the page presents the evidence
 * and leaves the judgement to a person.
 */
function IpMonitorAdmin() {
    const [addresses, setAddresses] = useState([]);
    const [summary, setSummary] = useState({ addresses: 0, requests: 0, blocked: 0, busyNow: 0 });
    const [defaultLimit, setDefaultLimit] = useState(60);
    const [filter, setFilter] = useState('busy');
    const [notice, setNotice] = useState(null);
    const [error, setError] = useState(null);
    const [limitDraft, setLimitDraft] = useState({ ip: null, value: '' });

    const authFetch = useCallback(async (path, options = {}) => {
        const token = localStorage.getItem('adminToken');
        const res = await fetch(`${API_URL}${path}`, {
            ...options,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        return { ok: res.ok, body: await res.json().catch(() => ({})) };
    }, []);

    const flash = (msg, isError = false) => {
        if (isError) { setError(msg); setNotice(null); } else { setNotice(msg); setError(null); }
        setTimeout(() => { setError(null); setNotice(null); }, 4000);
    };

    const load = useCallback(async () => {
        const { ok, body } = await authFetch('/admin/ip-monitor?limit=300');
        if (ok) {
            setAddresses(body.addresses || []);
            setSummary(body.summary || {});
            setDefaultLimit(body.defaultLimitPerMinute || 60);
        }
    }, [authFetch]);

    useEffect(() => { load(); }, [load]);

    // Traffic is the thing being watched, so the page keeps itself current.
    useEffect(() => {
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [load]);

    const block = async (ip, blocked) => {
        if (blocked && !window.confirm(
            `Block ${ip}?\n\nEveryone behind this address loses access to the public pages — ` +
            `if it is an office or mobile network, that may be several real customers.`)) return;
        const { ok, body } = await authFetch(`/admin/ip-monitor/${encodeURIComponent(ip)}/block`, {
            method: 'POST', body: JSON.stringify({ blocked })
        });
        if (!ok) return flash(body.error || 'Could not change that.', true);
        flash(blocked ? `${ip} blocked.` : `${ip} unblocked.`);
        load();
    };

    const saveLimit = async (ip) => {
        const { ok, body } = await authFetch(`/admin/ip-monitor/${encodeURIComponent(ip)}/limit`, {
            method: 'POST', body: JSON.stringify({ perMinute: limitDraft.value })
        });
        if (!ok) return flash(body.error || 'Could not set that limit.', true);
        flash(limitDraft.value ? `${ip} limited to ${limitDraft.value}/min.` : `${ip} back to the default limit.`);
        setLimitDraft({ ip: null, value: '' });
        load();
    };

    const forget = async (ip) => {
        await authFetch(`/admin/ip-monitor/${encodeURIComponent(ip)}`, { method: 'DELETE' });
        flash(`${ip} cleared.`);
        load();
    };

    const filters = [
        { key: 'busy', label: 'Active now', match: a => a.currentPerMinute > 0 },
        { key: 'heavy', label: 'Heaviest', match: a => a.peakPerMinute >= 20 },
        { key: 'blocked', label: 'Blocked', match: a => a.blocked },
        { key: 'limited', label: 'Limited', match: a => a.customLimit != null },
        { key: 'all', label: 'All', match: () => true },
    ];
    const active = filters.find(f => f.key === filter) || filters[0];
    const shown = addresses.filter(active.match);

    const rate = a => {
        const limit = a.customLimit != null ? a.customLimit : defaultLimit;
        if (a.currentPerMinute > limit) return 'over';
        if (a.currentPerMinute >= limit * 0.6) return 'high';
        return '';
    };

    return (
        <div className="tg-admin">
            <p className="tg-sub" style={{ marginTop: 0 }}>
                Traffic to the public pages — status lookups, self-switch and email change — grouped by address.
                Counts are shared across both server workers, so these are the real totals.
            </p>

            {notice && <div className="tg-note ok">{notice}</div>}
            {error && <div className="tg-note bad">{error}</div>}

            <div className="tg-summary">
                <span><b>{summary.addresses}</b> addresses seen</span>
                <span><b>{summary.busyNow}</b> busy right now</span>
                <span><b>{summary.blocked}</b> blocked</span>
                <span>default limit <b>{defaultLimit}/min</b></span>
            </div>

            <div className="tg-filters" style={{ marginBottom: 12 }}>
                {filters.map(f => (
                    <button key={f.key} className={`tg-chip ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
                        {f.label} <span className="tg-chip-n">{addresses.filter(f.match).length}</span>
                    </button>
                ))}
            </div>

            {!shown.length && (
                <div className="tg-card"><p className="tg-empty">Nothing in “{active.label}”.</p></div>
            )}

            {shown.map(a => (
                <div key={a.ip} className={`tg-prod ${a.blocked ? 'restricted' : rate(a) === 'over' ? 'unpriced' : ''}`}>
                    <div className="tg-prod-head">
                        <div className="tg-prod-name">
                            <code>{a.ip}</code>
                            {a.blocked && <span className="tg-tag" style={{ background: '#b91c1c' }}>blocked</span>}
                            {a.customLimit != null && <span className="tg-tag">{a.customLimit}/min</span>}
                        </div>
                        <div className="tg-prod-tags">
                            <span className={`tg-rate ${rate(a)}`}>{a.currentPerMinute}/min now</span>
                            <span className="tg-state">peak {a.peakPerMinute}/min</span>
                        </div>
                    </div>

                    {/* Who the address appears to be acting for. Public requests are
                        unauthenticated, but the email they carry belongs to a customer
                        with an owner, so they can still be attributed. */}
                    {a.resellers && a.resellers.length > 0 && (
                        <div className="tg-attrib">
                            <span className="tg-attrib-label">acting for</span>
                            {a.resellers.map(r => (
                                <span key={r.resellerId} className="tg-attrib-name">
                                    {r.username || `reseller ${r.resellerId}`}
                                    <b>{r.hits}</b>
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="tg-row-meta">
                        {a.totalRequests.toLocaleString()} requests total · last seen {new Date(a.lastSeenAt).toLocaleString()}
                        {a.lastPath && <> · <code>{a.lastPath}</code></>}
                        {a.lastUserAgent && <div className="tg-ua">{a.lastUserAgent}</div>}
                    </div>

                    <div className="tg-row-actions" style={{ marginTop: 10 }}>
                        <button className={`tg-btn sm ${a.blocked ? '' : 'danger'}`} onClick={() => block(a.ip, !a.blocked)}>
                            {a.blocked ? 'Unblock' : 'Block'}
                        </button>
                        <button className="tg-btn sm" onClick={() => setLimitDraft({ ip: a.ip, value: a.customLimit ?? '' })}>
                            Set limit
                        </button>
                        <button className="tg-btn sm" onClick={() => forget(a.ip)}>Clear</button>
                    </div>

                    {limitDraft.ip === a.ip && (
                        <div className="tg-inline">
                            <div className="tg-rate-edit">
                                <input type="number" min="1" value={limitDraft.value} placeholder={`default ${defaultLimit}`}
                                    onChange={e => setLimitDraft({ ...limitDraft, value: e.target.value })} />
                                <span>requests per minute — leave blank for the default</span>
                            </div>
                            <div className="tg-actions">
                                <button className="tg-btn primary sm" onClick={() => saveLimit(a.ip)}>Save limit</button>
                                <button className="tg-btn sm" onClick={() => setLimitDraft({ ip: null, value: '' })}>Cancel</button>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

export default IpMonitorAdmin;
